import type { DropdownOption } from '@acre/design-system'

import { areCredentialsProvided, getCredsFromMetadata } from '../User/Credentials/Credentials.helpers'

// Centralised config for referral partners, limited to referral partners that we have a specific flow for in the GI journey
type ReferralPartnerConfig = {
  messageKey: string
  insurerName?: string
  logoName?: string
}

export type ConfiguredReferralPartner = Extract<
  ReferralPartnerType,
  'UINSURE' | 'HIGOS' | 'BENNETT_ALEXANDER' | 'TOWERGATE' | 'GALLAGHER'
>

export const REFERRAL_PARTNER_CONFIG: Record<ConfiguredReferralPartner, ReferralPartnerConfig> = {
  UINSURE: { messageKey: 'uinsure' },
  HIGOS: { messageKey: 'higos', insurerName: 'Brown & Brown', logoName: 'Brown And Brown' },
  BENNETT_ALEXANDER: { messageKey: 'bennettAlexander', insurerName: 'Everywhen', logoName: 'Everywhen' },
  TOWERGATE: { messageKey: 'towergate' },
  GALLAGHER: { messageKey: 'gallagher' },
}

export const getPartnerLogoName = (partner: ConfiguredReferralPartner): string => {
  const config = REFERRAL_PARTNER_CONFIG[partner]
  return config?.logoName ?? partner
}

export const getPartnerInsurerName = (partner: ConfiguredReferralPartner): string | undefined => {
  const config = REFERRAL_PARTNER_CONFIG[partner]
  return config?.insurerName
}

export const getLogoNameForUnderwriter = (underwriter: string): string => {
  const config = Object.values(REFERRAL_PARTNER_CONFIG).find((c) => c.insurerName === underwriter)
  return config?.logoName ?? underwriter
}

export const getReferralPartnerOptions = (
  formatMessage: (key: string) => string,
): DropdownOption<ReferralPartnerType>[] =>
  Object.entries(REFERRAL_PARTNER_CONFIG)
    .map(([value, config]) => ({
      value: value as ReferralPartnerType,
      label: formatMessage(`generalInsurance.partners.${config.messageKey}`),
    }))
    .sort((a, b) => a.label.localeCompare(b.label))

export const postRecommendationGIProductStages: GeneralInsuranceQuoteStage[] = [
  'SELECTED',
  'APPLICATION_IN_PROGRESS',
  'CURRENT',
]

export const hasCredentialsForGIProvider = (
  user: Maybe<User>,
  provider: Maybe<GeneralInsuranceProvider | 'MANUAL'>,
) => {
  if (!provider) return false

  if (provider === 'LV') {
    return true
  }
  if (provider === 'PAYMENTSHIELD' || provider === 'UINSURE') {
    const credentials = getCredsFromMetadata(user?.metadata || [], provider, false)
    return areCredentialsProvided(credentials)
  }

  return false
}

type FormattedProviderName = 'Uinsure' | 'PaymentShield' | 'Home In One' | 'LV= IFA' | 'Unknown provider'

export const getProviderName = (name: string): FormattedProviderName => {
  switch (name.toUpperCase()) {
    case 'UINSURE':
      return 'Uinsure'
    case 'PAYMENTSHIELD':
      return 'PaymentShield'
    case 'HOMEINONE':
      return 'Home In One'
    case 'LV':
      return 'LV= IFA'
    default:
      return 'Unknown provider'
  }
}

export const getProviderLink = (name: string) => {
  switch (name) {
    case 'UINSURE':
      return 'https://uinsure.co.uk/home-insurance/'
    case 'PAYMENTSHIELD':
      return 'https://www.paymentshield.co.uk/your-policy-documents/home-insurance'
    default:
      return undefined
  }
}
